/** SUNTRONIX ONE — Commercial → CSAT & Feedback command centre. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Smile, AlertTriangle, Sparkles, Gauge, TrendingUp, X } from "lucide-react";

const RTINT = { "VERY GOOD": "bg-emerald-100 text-emerald-800", GOOD: "bg-teal-100 text-teal-800",
  AVERAGE: "bg-amber-100 text-amber-800", POOR: "bg-orange-100 text-orange-800", "VERY POOR": "bg-rose-100 text-rose-800" };

const Kpi = ({ label, value, sub, tint, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-1 text-2xl font-semibold ${tint || "text-slate-900"}`}>{value}</div>
    {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
  </div>
);

export default function CsatCentre() {
  const [tab, setTab] = useState("overview");
  const [an, setAn] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [alerts, setAlerts] = useState({ items: [], open_count: 0 });
  const [ai, setAi] = useState({ loading: false, res: null });

  const load = useCallback(() => {
    api.get("/csat/analytics").then(({ data }) => setAn(data)).catch(() => {});
    api.get("/csat/requests").then(({ data }) => setReqs(data.items || [])).catch(() => {});
    api.get("/csat/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runAi = async () => {
    setAi({ loading: true, res: null });
    try { const { data } = await api.post("/csat/ai", { query: "Summarise customer feedback" });
      setAi({ loading: false, res: data });
    } catch { setAi({ loading: false, res: null }); toast.error("AI failed"); }
  };
  const resolveAlert = async (id) => {
    await api.patch(`/csat/alerts/${id}`, { status: "RESOLVED", resolution_note: "Followed up with customer" });
    toast.success("Alert resolved"); load();
  };

  const csat = an?.csat_overall;
  const Grp = ({ title, rows, testId }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="mb-2 text-[12px] font-semibold">{title}</div>
      {(rows || []).map((r) => (
        <div key={r.key} className="flex items-center gap-2 border-t border-slate-100 py-1 text-[11px]">
          <span className="flex-1 truncate text-slate-600">{r.key}</span>
          <span className="text-slate-400">{r.responses}</span>
          <span className="w-12 text-right font-semibold text-slate-800">{r.csat != null ? `${r.csat}%` : "—"}</span>
        </div>
      ))}
      {!(rows || []).length && <div className="text-[11px] text-slate-400">No data.</div>}
    </div>
  );

  return (
    <div className="mx-auto max-w-[1200px] space-y-4" data-testid="csat-centre-page">
      <header className="flex flex-wrap items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-teal-900 text-white"><Smile className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-heading text-xl font-semibold">CSAT &amp; Customer Feedback</div>
          <div className="text-[11px] text-slate-500">Feedback tied to tickets, deployments, deliveries, recovery, field service &amp; orders</div>
        </div>
        {alerts.open_count > 0 && (
          <span className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-2 py-1 text-[11px] font-semibold text-rose-700" data-testid="csat-open-alerts">
            <AlertTriangle className="h-3.5 w-3.5" /> {alerts.open_count} CX alert{alerts.open_count > 1 ? "s" : ""}
          </span>
        )}
      </header>

      <div className="flex gap-1 border-b border-slate-100 text-[12px]">
        {[["overview", "Overview"], ["requests", `Feedback (${reqs.length})`], ["alerts", `CX Alerts (${alerts.open_count})`], ["ai", "ONE AI"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`csat-tab-${k}`}
            className={`px-3 py-2 font-semibold border-b-2 -mb-px ${tab === k ? "border-teal-700 text-slate-900" : "border-transparent text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && an && (
        <div className="space-y-3" data-testid="csat-overview">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Kpi label="CSAT" value={csat != null ? `${csat}%` : "—"} sub="top-2-box" tint="text-emerald-700" testId="csat-kpi-overall" />
            <Kpi label="Responses" value={an.responded} sub={`of ${an.sent} sent · ${an.response_rate}%`} testId="csat-kpi-responses" />
            <Kpi label="NPS" value={an.nps?.score != null ? an.nps.score : "—"} sub={`${an.nps?.responses || 0} responses (separate)`} tint="text-blue-700" testId="csat-kpi-nps" />
            <Kpi label="Period" value={`${an.period_days}d`} testId="csat-kpi-period" />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-2 text-[12px] font-semibold">Rating distribution</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(an.distribution || {}).map(([k, v]) => (
                <span key={k} className={`rounded px-2 py-1 text-[11px] font-semibold ${RTINT[k]}`}>{k}: {v}</span>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Grp title="CSAT by Service Type" rows={an.by_service_type} testId="csat-by-service" />
            <Grp title="CSAT by Customer" rows={an.by_customer} testId="csat-by-customer" />
            <Grp title="CSAT by Agent" rows={an.by_agent} testId="csat-by-agent" />
            <Grp title="CSAT by Engineer" rows={an.by_engineer} testId="csat-by-engineer" />
            <Grp title="CSAT by Ticket Type" rows={an.by_ticket_type} testId="csat-by-ticket" />
          </div>
        </div>
      )}

      {tab === "requests" && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="csat-requests-list">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
              <tr><th className="px-3 py-2">Source</th><th className="px-3 py-2">Customer</th><th className="px-3 py-2">Service</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Rating</th><th className="px-3 py-2">Comment</th></tr>
            </thead>
            <tbody>
              {reqs.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`csat-req-${r.id}`}>
                  <td className="px-3 py-1.5"><span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold">{r.source_type}</span> <span className="text-[9px] text-slate-400">{r.reference}</span></td>
                  <td className="px-3 py-1.5">{r.customer_name || "—"}</td>
                  <td className="px-3 py-1.5">{r.service_type}</td>
                  <td className="px-3 py-1.5"><span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${r.status === "RESPONDED" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>{r.status}</span></td>
                  <td className="px-3 py-1.5">{r.rating_label ? <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${RTINT[r.rating_label]}`}>{r.rating_label}</span> : "—"}</td>
                  <td className="px-3 py-1.5 max-w-[200px] truncate text-slate-500">{r.comment || "—"}</td>
                </tr>
              ))}
              {!reqs.length && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No feedback requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "alerts" && (
        <div className="space-y-2" data-testid="csat-alerts-list">
          {(alerts.items || []).map((a) => (
            <div key={a.id} className="rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid={`cx-alert-${a.id}`}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-rose-600" />
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${RTINT[a.rating_label]}`}>{a.rating_label}</span>
                <span className="text-[12px] font-semibold text-slate-900">{a.customer_name || "Customer"}</span>
                <span className="text-[10px] text-slate-500">{a.source_type} · {a.service_type} {a.reference ? `· ${a.reference}` : ""}</span>
                <span className={`ml-auto rounded px-1.5 py-0.5 text-[9px] font-semibold ${a.status === "RESOLVED" ? "bg-emerald-100 text-emerald-800" : "bg-white text-rose-700"}`}>{a.status}</span>
              </div>
              {a.comment && <div className="mt-1 text-[11px] text-slate-700">"{a.comment}"</div>}
              {a.manager_followup && a.status !== "RESOLVED" && <div className="mt-1 text-[10px] font-semibold text-rose-700">Manager follow-up required</div>}
              {a.status !== "RESOLVED" && (
                <button onClick={() => resolveAlert(a.id)} data-testid={`cx-alert-resolve-${a.id}`}
                  className="mt-2 rounded-md border border-rose-400 px-2 py-1 text-[10px] font-semibold text-rose-700">Mark followed-up / resolved</button>
              )}
            </div>
          ))}
          {!(alerts.items || []).length && <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-[12px] text-slate-400">No customer-experience alerts. 🎉</div>}
        </div>
      )}

      {tab === "ai" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="csat-ai">
          <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold"><Sparkles className="h-4 w-4 text-violet-600" /> Feedback insights</div>
          <button onClick={runAi} data-testid="csat-ai-run" className="rounded-md bg-violet-600 px-3 py-1.5 text-[11px] font-semibold text-white">Summarise feedback</button>
          {ai.loading && <div className="mt-3 text-[11px] text-slate-500">Analysing…</div>}
          {ai.res && (
            <div className="mt-3 space-y-2 text-[12px]" data-testid="csat-ai-result">
              <div className="rounded-md border border-violet-200 bg-violet-50 p-3">{ai.res.answer}</div>
              <AiList title="Common complaints" items={ai.res.complaints} tint="text-rose-700" />
              <AiList title="Positive feedback" items={ai.res.positives} tint="text-emerald-700" />
              <AiList title="Areas requiring attention" items={ai.res.attention} tint="text-amber-700" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
const AiList = ({ title, items, tint }) => (
  (items || []).length ? (
    <div><div className={`text-[11px] font-semibold ${tint}`}>{title}</div>
      <ul className="ml-4 list-disc text-[11px] text-slate-600">{items.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
  ) : null
);
